/**
 * Client-side ability + slot-order catalog — AUTO-DERIVED from content
 * (#274). Same build-time-glob + narrow-schema-strip pattern as
 * `catalog.ts`'s champion catalog (#239): read the raw JSON text at BUILD
 * TIME via `import.meta.glob`, then parse it down to ONLY the fields a
 * player already learns the instant an ability is cast (name/slug/id) —
 * never the balance-sensitive shape (damage, cooldown, cost, range,
 * effects, schools, targeting...).
 *
 * WHY THIS EXISTS: `abilityLabels.ts` used to hand-maintain a
 * champion-id → ability-name-per-slot table. That table drifted the moment
 * a kit changed without a matching edit here (#271: Rogue had zero entries;
 * #334: Priest/Mage/Afflictor were entirely missing). This module makes that
 * whole bug class structurally impossible: names + slot order are derived
 * from content, so a new/changed champion or ability needs zero client edits.
 *
 * TWO SEPARATE GLOBS, TWO SEPARATE NARROW SCHEMAS:
 *  - ability JSON files (`packages/content/data/abilities/*.json`) are named
 *    by SLUG, not id, so there is no `<id>.json` shortcut — every ability
 *    file is read and indexed by its `id` field into `ABILITY_META_BY_ID`.
 *  - champion JSON files' `abilities` array (id list, index = slot) gives the
 *    slot order. `catalog.ts`'s `CHAMPION_META_SCHEMA` already reads champion
 *    JSON but STRIPS `abilities` (picker-only concern: id/slug/name). This
 *    module parses champion JSON again with its OWN narrow schema that keeps
 *    `id` + `abilities` instead — a separate parse/type, not a loosening of
 *    the picker's schema.
 *
 * FAIL LOUD: a champion's `abilities` array referencing an ability id with no
 * matching ability JSON throws at module load (build/test time), mirroring
 * `catalog.ts`'s "malformed champion JSON throws here" convention. Content is
 * already validated upstream by `@pit/content`'s own tests + the server
 * loader, so this can only fire if that gate was skipped.
 */

import { z } from "zod";
import {
  ABILITY_ID_SCHEMA,
  AURA_DEF_ID_SCHEMA,
  CHAMPION_ID_SCHEMA,
  SLUG_SCHEMA,
  type AbilityId,
  type AuraDefId,
  type ChampionId,
} from "@pit/protocol";

/** Auto-derived, name-only ability metadata — never anything balance-sensitive. */
export interface AbilityMeta {
  readonly id: AbilityId;
  readonly slug: string;
  readonly name: string;
}

/**
 * Minimal ability metadata schema. `z.object` (not `strictObject`) strips
 * `effects`/`cost`/`rangeUnits`/`cooldownTicks`/`castTimeTicks`/`schools`/
 * `targetType`/`losPolicy`/`facingPolicy`/`aimSnapshot`/`description`/etc., so
 * none of that ever reaches the client bundle. Mirrors `catalog.ts`'s
 * `CHAMPION_META_SCHEMA`.
 */
const ABILITY_META_SCHEMA = z.object({
  id: ABILITY_ID_SCHEMA,
  slug: SLUG_SCHEMA,
  name: z.string().min(1).max(64),
});

/**
 * #76: the ONE `SlotCondition` variant the client needs to mirror —
 * `holdsAura` (a well-known aura def id). `always` is schema-valid content
 * (per `@pit/protocol`'s closed union) but has no shipped consumer yet; a
 * champion authoring it would simply never match here (`auraDefId` stays
 * `undefined`), which is a harmless no-op resolve-to-base, not a crash.
 */
const SLOT_CONDITION_SCHEMA = z.union([
  z.object({ kind: z.literal("holdsAura"), aura: AURA_DEF_ID_SCHEMA }),
  z.object({ kind: z.literal("always") }),
]);

/**
 * #76: a client-side MIRROR of `ConditionalSlot` — just enough to decide
 * which ability's NAME/icon to show for a slot, never anything the sim
 * itself doesn't already put on the wire (the caster's own aura list,
 * #91/`SelfAuraBar`). `rules` keeps only the `holdsAura` variant's aura id
 * (an `always` rule has no `aura` to check and is simply never matched by
 * `resolveDisplayAbility` below — cosmetically inert, never a crash).
 */
const CONDITIONAL_SLOT_SCHEMA = z.object({
  slot: z.number().int().gte(0),
  rules: z
    .array(z.object({ when: SLOT_CONDITION_SCHEMA, use: ABILITY_ID_SCHEMA }))
    .min(1),
});

/**
 * Slot-order-only champion schema: just enough to map a champion id to its
 * ability id list (index = slot) + (#76) its conditional-slot overrides. A
 * SEPARATE parse/type from `catalog.ts`'s `CHAMPION_META_SCHEMA` (which keeps
 * `slug`/`name` but strips `abilities`) — not a loosening of it.
 */
const CHAMPION_SLOTS_SCHEMA = z.object({
  id: CHAMPION_ID_SCHEMA,
  abilities: z.array(ABILITY_ID_SCHEMA).min(1),
  conditionalSlots: z.array(CONDITIONAL_SLOT_SCHEMA).optional(),
});

/** #76: a client-side conditional-slot rule — just the aura id + override
 *  ability id `resolveDisplayAbility` needs (an `always`/unrecognized rule
 *  carries `auraDefId: undefined` and is simply never matched). */
export interface ConditionalSlotRuleMeta {
  readonly auraDefId: AuraDefId | undefined;
  readonly useAbilityId: AbilityId;
}

/** #76: a client-side conditional-slot entry — mirrors `ConditionalSlot`. */
export interface ConditionalSlotMeta {
  readonly slot: number;
  readonly rules: readonly ConditionalSlotRuleMeta[];
}

/** Exported for tests only — the slot-order-only shape parsed per champion. */
export interface ChampionSlots {
  readonly id: ChampionId;
  readonly abilities: readonly AbilityId[];
  readonly conditionalSlots: readonly ConditionalSlotMeta[];
}

/**
 * Build-time map of every on-disk ability JSON → its RAW text, parsed down to
 * `{ id, slug, name }`. Same literal-`import.meta.glob` rule as `catalog.ts`:
 * the call MUST be the literal form for Vite's static transform.
 */
function loadContentAbilities(): readonly AbilityMeta[] {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/abilities/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out: AbilityMeta[] = [];
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const meta = ABILITY_META_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.push({ id: meta.id, slug: meta.slug, name: meta.name });
  }
  return out;
}

/** Every ability shipped in content, `{id, slug, name}` only. */
export const ALL_CONTENT_ABILITIES: readonly AbilityMeta[] = loadContentAbilities();

/** Ability metadata indexed by its opaque id, for O(1) slot resolution. */
export const ABILITY_META_BY_ID: ReadonlyMap<AbilityId, AbilityMeta> = new Map(
  ALL_CONTENT_ABILITIES.map((a) => [a.id, a]),
);

/**
 * Build-time map of every on-disk champion JSON → its RAW text, parsed down to
 * `{ id, abilities, conditionalSlots }` (slot order + #76 overrides only —
 * see `CHAMPION_SLOTS_SCHEMA`).
 */
function loadChampionSlots(): readonly ChampionSlots[] {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/champions/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out: ChampionSlots[] = [];
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const parsed = CHAMPION_SLOTS_SCHEMA.parse(JSON.parse(raw) as unknown);
    const conditionalSlots = (parsed.conditionalSlots ?? []).map((cs) => ({
      slot: cs.slot,
      rules: cs.rules.map((rule) => ({
        auraDefId: rule.when.kind === "holdsAura" ? rule.when.aura : undefined,
        useAbilityId: rule.use,
      })),
    }));
    out.push({ id: parsed.id, abilities: parsed.abilities, conditionalSlots });
  }
  return out;
}

/**
 * Champion id → ability NAME per slot (index i = slot i): resolve each
 * champion's ability id list, then resolve each id to its ability's `name`
 * via `abilityIndex`. A malformed/missing ability reference (id in a
 * champion's `abilities` array with no matching ability JSON) throws LOUD —
 * exported (pure, no glob I/O) so tests can exercise both the happy path and
 * the throw without needing to corrupt real content.
 */
export function deriveAbilityNamesByChampion(
  championSlots: readonly ChampionSlots[],
  abilityIndex: ReadonlyMap<AbilityId, AbilityMeta>,
): ReadonlyMap<ChampionId, readonly string[]> {
  return new Map(
    championSlots.map(({ id: championId, abilities: abilityIds }) => {
      const names = abilityIds.map((abilityId) => {
        const meta = abilityIndex.get(abilityId);
        if (meta === undefined) {
          throw new Error(
            `champion ${championId} references ability id ${abilityId}, which has ` +
              `no matching ability JSON under packages/content/data/abilities/*.json`,
          );
        }
        return meta.name;
      });
      return [championId, names];
    }),
  );
}

/**
 * Champion id → ability id list in SLOT order (index i = slot i). Exported
 * for tests only (cross-checking derived slot order against `@pit/content`).
 */
export const CHAMPION_SLOTS: readonly ChampionSlots[] = loadChampionSlots();

/**
 * Champion id → ability NAME per slot (index i = slot i), fully derived from
 * content. Computed eagerly at module load so a malformed/missing ability
 * reference throws LOUD here, at build/test time, rather than lazily on
 * first render.
 */
export const ABILITY_NAMES_BY_CHAMPION: ReadonlyMap<ChampionId, readonly string[]> =
  deriveAbilityNamesByChampion(CHAMPION_SLOTS, ABILITY_META_BY_ID);

/** Champion id → its `conditionalSlots` (#76), for O(1) per-champion lookup. */
export const CONDITIONAL_SLOTS_BY_CHAMPION: ReadonlyMap<ChampionId, readonly ConditionalSlotMeta[]> =
  new Map(CHAMPION_SLOTS.map((c) => [c.id, c.conditionalSlots]));

/** Champion id → its full ability id list in slot order, for O(1) lookup
 *  (mirrors `CONDITIONAL_SLOTS_BY_CHAMPION`'s shape). */
const ABILITY_IDS_BY_CHAMPION: ReadonlyMap<ChampionId, readonly AbilityId[]> = new Map(
  CHAMPION_SLOTS.map((c) => [c.id, c.abilities]),
);

/**
 * #76: does `auraDefId` appear, UNEXPIRED, in `selfAuras` at `tick`? The
 * client-side mirror of `@pit/sim`'s `hasActiveAuraDef`/`isStealthedWire`
 * (`stealthVisibility.ts`) — same expiry-only, bundle-free rule — generalized
 * to an arbitrary aura id instead of the one hardcoded `STEALTH_AURA_DEF_ID`.
 * PASSIVE MIRROR ONLY: the sim's OWN resolution at actual cast time is what
 * matters; this never feeds back into any cast/input decision.
 */
function hasActiveAura(
  selfAuras: readonly { readonly def: AuraDefId; readonly expiresAtTick: number | null }[],
  auraDefId: AuraDefId,
  tick: number,
): boolean {
  for (const a of selfAuras) {
    if (a.def !== auraDefId) continue;
    if (a.expiresAtTick === null || tick < a.expiresAtTick) return true;
  }
  return false;
}

/**
 * #76: resolve the ability actually DISPLAYED for `championId`'s `slot`,
 * given the VIEWER'S OWN current auras (already on the wire, #91's
 * `WireEntity.auras` — no new wire field). Mirrors `resolveSlotAbility`'s
 * (`@pit/sim` `cast.ts`) `holdsAura` rule exactly, read-only and COSMETIC —
 * a client that renders the wrong name/icon for one frame during a state
 * transition (e.g. a stealth-break landing mid-tick before the next
 * snapshot) is a presentation glitch, never a correctness bug: the sim's own
 * resolution at actual cast time is authoritative regardless of what this
 * function returns. First-match-wins over `rules`, exactly like the sim.
 *
 * Falls back to the slot's ordinary (unconditional) ability meta when the
 * champion has no `conditionalSlots` entry for `slot`, no rule matches, or
 * the slot/ability itself can't be resolved (defensive — mirrors
 * `abilityLabel`'s existing null-on-miss convention).
 */
export function resolveDisplayAbilityMeta(
  championId: ChampionId,
  slot: number,
  selfAuras: readonly { readonly def: AuraDefId; readonly expiresAtTick: number | null }[],
  tick: number,
): AbilityMeta | null {
  const baseId = ABILITY_IDS_BY_CHAMPION.get(championId)?.[slot];
  const base = baseId !== undefined ? (ABILITY_META_BY_ID.get(baseId) ?? null) : null;

  const conditional = (CONDITIONAL_SLOTS_BY_CHAMPION.get(championId) ?? []).find(
    (cs) => cs.slot === slot,
  );
  if (conditional === undefined) return base;

  for (const rule of conditional.rules) {
    if (rule.auraDefId !== undefined && hasActiveAura(selfAuras, rule.auraDefId, tick)) {
      return ABILITY_META_BY_ID.get(rule.useAbilityId) ?? base;
    }
  }
  return base;
}
